/******************************************************************************

Copyright (c) 2016-2018, Highsoft

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY
CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

******************************************************************************/
import Events from './../../core/highed.events';
import { cr, ap, setStyle, on, getElementSize, getElementPosition } from './../../core/highed.dom';
import {
  ready,
  merge,
  isFn,
  isStr,
  hexToRgb,
  setPixel,
  rgbaObjToHex,
  rgbObjToHex,
  hsvToRgb,
  rgbToHsv,
  hexToRgba
} from './../../core/highcharts-editor';
import Movable from './../highed.mover';
import Dimmer from './../highed.dimmer';
import ColorSlider from './highed.colorslider';

/**
 * A HSV based color picker
 */
export default class CustomColorPicker {
  /** HSV color picker
   * Component to pick colors using HSV values.
   * @param {number} xPos - the x position to display the picker at
   * @param {number} yPos - the y position to display the picker at
   * @param {function} callback - callback for when the color changes
   * @param {object} props - properties for the color picker
   */
  constructor(xPos, yPos, currentColorVal, callback, props) {
    this.container = cr('div', 'highed-custom-colorpicker');
    this.palette = cr('canvas', 'highed-palette');
    this.palette.width = 220;
    this.palette.height = 160;
    this.events = Events();
    this.paletteIndicator = cr('div', 'highed-palette-indicator');
    this.hueSlider = cr('div', 'highed-hue-slider');
    this.manualInput = cr('input', 'highed-field-input highed-hue-manual');
    this.colorIndicator = cr('div', 'highed-color-indicator');
    this.colorTransparentIndicator = cr('div', 'highed-color-transparent-indicator');
    this.props = props || {};

    this.hueSliderBody = cr('canvas', 'highed-hue-slider-body');
    this.hueIndicator = cr('div', 'highed-hue-indicator');
    this.hueSliderBody.width = 220;
    this.hueSliderBody.height = 16;

    this.state = {
      palette: {
        indicator: {
          position: { x: 0, y: 0 },
          size: { w: 0, h: 0 }
        },
        size: { w: 0, h: 0 },
        xOffset: 0,
        yOffset: 0
      },
      hue: {
        indicator: { position: { x: 0 }, size: { w: 0, h: 0 } },
        size: { w: 0, h: 0 },
        xOffset: 0,
        yOffset: 0
      },
      selectedColor: {
        r: 0,
        g: 0,
        b: 0,
        a: 255
      },
      colorHue: 0, //[0-360]
      colorSaturation: 1, //[0-1]
      colorValue: 1 //[0-1]
    };

    const calcAlpha = (scalar) => {
      let color = { ...this.state.selectedColor };
      color.a = scalar * 255;
      return color;
    };

    const alphaCallback = (color) => {
      this.state.selectedColor.a = color.a;
      this.draw();
    };

    if (currentColorVal === '') currentColorVal = '#ffffff';
    this.alphaSlider = new ColorSlider(
      10,
      200,
      220,
      16,
      calcAlpha.bind(this),
      this.safeHexToRgba(currentColorVal).a / 255,
      this.container,
      alphaCallback
    );
    this.alphaBackground = cr('canvas', 'highed-alpha-background');

    this.hueMover = Movable(this.hueIndicator, 'X', true, this.hueSlider);
    this.paletteMover = Movable(this.paletteIndicator, 'XY', true, this.palette);

    this.dimmer = Dimmer(this.remove.bind(this), true, true, 100000);

    this.ctx = this.palette.getContext('2d');
    this.sliderCtx = this.hueSliderBody.getContext('2d');

    this.currentColorVal = currentColorVal;
    this.callback = callback;

    ready(() => ap(document.body, this.props.parent || this.container));

    let containerHeight = getElementSize(this.container).h;
    this.xPos = xPos;
    this.yPos = yPos + containerHeight < window.innerHeight ? yPos : yPos - containerHeight;

    setStyle(this.container, { left: this.xPos + 'px', top: this.yPos + 'px' });

    this.build();
    this.setup();
    //this.draw();
  }

  /**
   * Sets the hue variable. Should be within [0, 360]
   * @param {number} hue value
   */
  set _colorHue(hue) {
    this.state.colorHue = hue;
    this.draw();
  }

  safeHexToRgba(hex) {
    if (!hex) {
      return {
        r: 0,
        g: 0,
        b: 0,
        a: 255
      };
    }
    let hasHashtag = hex.startsWith('#');
    if ((hex.length == 3 && !hasHashtag) || (hex.length == 4 && hasHashtag)) {
      let newHex = '#';
      hex = hex.replace('#', '');
      for (let c of hex) {
        newHex += c + c;
      }
      hex = newHex;
    }

    if (hex.length != 9) {
      return merge({ a: 255 }, hexToRgb(hex));
    } else {
      return hexToRgba(hex);
    }
  }

  /**
   * Sets the position of the palette indicator
   * @param {number} x - x position
   * @param {number} y - y position
   */
  setPaletteIndicatorPosition(x, y, draw = true) {
    this.state.palette.indicator.position = { x, y };
    this.draw(draw);
  }

  /**
   * Sets the position of the hue indicator
   * @param {number} x - x position
   */
  setHueIndicatorPosition(x, draw = true) {
    this.state.hue.indicator.position.x = x;
    this.draw(draw);
  }

  /**
   * Builds the DOM node
   */
  build() {
    ap(
      this.container,
      this.palette,
      this.paletteIndicator,
      this.hueSlider,
      this.alphaSlider.container,
      this.manualInput,
      this.colorTransparentIndicator,
      this.colorIndicator
    );
    ap(this.hueSlider, this.hueIndicator);
    ap(this.hueSlider, this.hueSliderBody);
  }

  /**
   * Draws the palette, sets the manual input value and positions the indicators
   */
  draw(doCallback = true) {
    this.calcSelectedColor();
    // Palette
    let imageData = this.ctx.getImageData(0, 0, 220, 160);

    for (let x = 0; x < imageData.width; x++) {
      for (let y = 0; y < imageData.height; y++) {
        let saturation = x / imageData.width,
          value = (imageData.height - y) / imageData.height;
        setPixel(imageData, x, y, hsvToRgb(this.state.colorHue, saturation, value));
      }
    }

    this.ctx.putImageData(imageData, 0, 0);

    // Hue Slider Indicator
    let hue = hsvToRgb(this.state.colorHue, 1, 1);
    let hueIndPos = this.state.hue.indicator.position.x - this.state.hue.indicator.size.w / 2;
    setStyle(this.hueIndicator, {
      left: hueIndPos + 'px',
      backgroundColor: `rgb(${hue.r},${hue.g},${hue.b})`
    });

    // Alpha slider
    this.alphaSlider.draw();

    // Palette Indicator
    let palette = this.state.palette;

    let xPos = palette.indicator.position.x,
      yPos = palette.indicator.position.y;

    let { r, g, b, a } = this.state.selectedColor;
    setStyle(this.paletteIndicator, {
      left: xPos + 'px',
      top: yPos + 'px',
      backgroundColor: `rgb(${r},${g},${b})`
    });

    let color = '';
    // Manual Input
    if (a == 255) {
      color = rgbObjToHex(this.state.selectedColor);
    } else {
      color = rgbaObjToHex(this.state.selectedColor);
    }

    this.manualInput.value = color;

    // Color indicator
    setStyle(this.colorIndicator, {
      backgroundColor: color
    });

    // Callback
    if (doCallback) this.doCallback(color);
  }

  /**
   * Performs initial setup of the color picker
   */
  setup() {
    on(this.palette, 'click', (event) => {
      let x = event.clientX - this.state.palette.xOffset,
        y = event.clientY - this.state.palette.yOffset;
      this.calcSelectedColor();
      this.setPaletteIndicatorPosition(x, y);
    });

    this.paletteMover.on('Moving', (x, y) => {
      this.setPaletteIndicatorPosition(x, y);
    });

    on(this.hueSliderBody, 'click', (event) => {
      let x = event.clientX - this.state.hue.xOffset;
      this.setHueIndicatorPosition(x);
    });

    this.hueMover.on('Moving', (x) => {
      this.setHueIndicatorPosition(x);
    });

    on(this.manualInput, 'change', (event) => {
      this.setColorFromHex(event.target.value);
    });

    this.drawHueSlider();
    this.setupInitialState();
  }

  /**
   * Initializes the object's state
   */
  setupInitialState() {
    let palette = this.state.palette;
    palette.size = getElementSize(this.palette);
    palette.indicator.size = getElementSize(this.paletteIndicator);
    let paletteBCR = this.palette.getBoundingClientRect();
    palette.xOffset = paletteBCR.left;
    palette.yOffset = paletteBCR.top;

    let hue = this.state.hue;
    hue.size = getElementSize(this.hueSlider);
    hue.indicator.size = getElementSize(this.hueIndicator);
    let hueSliderBCR = this.hueSliderBody.getBoundingClientRect();
    hue.xOffset = hueSliderBCR.x;
    hue.yOffset = hueSliderBCR.y;

    if (this.currentColorVal) {
      this.setColorFromHex(this.currentColorVal, false);
    }
  }

  /**
   * Calculates the currently selected color based on indicator positions
   */
  calcSelectedColor() {
    let { x, y } = this.state.palette.indicator.position;
    this.state.colorHue = (this.state.hue.indicator.position.x / this.state.hue.size.w) * 360;
    this.state.colorSaturation = x / this.state.palette.size.w;
    this.state.colorValue = (this.state.palette.size.h - y) / this.state.palette.size.h;

    this.state.selectedColor = hsvToRgb(this.state.colorHue, this.state.colorSaturation, this.state.colorValue);

    this.state.selectedColor.a = this.alphaSlider.sliderPos * 255;
  }

  /**
   * Draws the hue slider
   */
  drawHueSlider() {
    let sliderImageData = this.sliderCtx.getImageData(0, 0, this.hueSliderBody.width, this.hueSliderBody.height);

    for (let x = 0; x < this.hueSliderBody.width; x++) {
      let hue = (x / this.hueSliderBody.width) * 360;
      let color = hsvToRgb(hue, 1, 1);
      for (let y = 0; y < this.hueSliderBody.height; y++) {
        setPixel(sliderImageData, x, y, color);
      }
    }

    this.sliderCtx.putImageData(sliderImageData, 0, 0);
  }

  /**
   * Calls the callback
   */
  doCallback(color) {
    if (isFn(this.callback) && isStr(color)) {
      this.callback(color);
    }
  }

  /**
   * Removes the color picker from the DOM
   */
  remove() {
    this.container.remove();
    this.events.emit('Close');
  }

  setColorFromHex(hexColor, draw = true) {
    let rgbColor = hexColor.length > 7 ? hexToRgba(hexColor) : hexToRgb(hexColor);
    let hsvColor = rgbToHsv(rgbColor);

    this.setHueIndicatorPosition((hsvColor.h / 360) * this.state.hue.size.w, draw);

    let paletteX = hsvColor.s * this.state.palette.size.w;
    let paletteY = this.state.palette.size.h - hsvColor.v * this.state.palette.size.h;
    this.setPaletteIndicatorPosition(paletteX, paletteY, draw);
  }

  on(type, fn) {
    this.events.on(type, fn);
  }
}
